import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaTrophy, FaUser, FaCoins } from 'react-icons/fa';
import { useAppSelector } from '../store/hooks';

interface GameSession {
  id: number;
  challengeId: number;
  gameId: number;
  gameName: string;
  gameType: string;
  player1Id: number;
  player1Username: string;
  player1Avatar?: string;
  player1Ready: boolean;
  player2Id: number;
  player2Username: string;
  player2Avatar?: string;
  player2Ready: boolean;
  status: string;
  currentTurn?: string;
  gameState?: string;
  multiSigWalletId?: number;
  platformFee: number;
  payoutAmount: number;
  totalStake?: number;
  winnerId?: number | null;
  result?: string | null;
  createdAt: string;
}

const P2PGamePlayer: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<GameSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [gameBoard, setGameBoard] = useState<string[]>(Array(9).fill(''));
  const [currentTurn, setCurrentTurn] = useState<number | null>(null);
  const [gameStatus, setGameStatus] = useState<string>('IN_PROGRESS');
  const [winner, setWinner] = useState<number | null>(null);
  
  // Football trivia state (for Tiki Taka Toe)
  const [showFootballModal, setShowFootballModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState<number | null>(null);
  const [footballerInput, setFootballerInput] = useState('');
  const [playerAnswers, setPlayerAnswers] = useState<{[key: number]: string}>({});
  
  // Battleships state
  const [battleshipsState, setBattleshipsState] = useState<any>(null);
  const [selectedShip, setSelectedShip] = useState<{size: number, count: number} | null>(null);
  const [shipOrientation, setShipOrientation] = useState<boolean>(true); // true = horizontal
  
  const profile = useAppSelector((state: any) => state.profile.profile);
  
  // Football trivia criteria
  const rowCriteria = ["Premier League", "La Liga", "Serie A"];
  const columnCriteria = ["Striker", "Midfielder", "Defender"];
  
  // Footballer database for validation
  const footballerDatabase: {[key: string]: {[key: string]: string[]}} = {
    "Premier League": {
      "Striker": ["Harry Kane", "Erling Haaland", "Mohamed Salah", "Son Heung-min", "Ollie Watkins", "Ivan Toney"],
      "Midfielder": ["Kevin De Bruyne", "Bruno Fernandes", "Martin Ødegaard", "Declan Rice", "Rodri", "James Maddison"],
      "Defender": ["Virgil van Dijk", "Ruben Dias", "William Saliba", "John Stones", "Kyle Walker", "Ben White"]
    },
    "La Liga": {
      "Striker": ["Robert Lewandowski", "Karim Benzema", "Antoine Griezmann", "Vinicius Jr", "Joselu", "Álvaro Morata"],
      "Midfielder": ["Luka Modrić", "Pedri", "Federico Valverde", "Gavi", "Jude Bellingham", "Toni Kroos"],
      "Defender": ["Ronald Araújo", "Éder Militão", "David Alaba", "Jules Koundé", "Antonio Rüdiger", "Marcos Alonso"]
    },
    "Serie A": {
      "Striker": ["Victor Osimhen", "Lautaro Martínez", "Dusan Vlahovic", "Olivier Giroud", "Romelu Lukaku", "Ciro Immobile"],
      "Midfielder": ["Nicolò Barella", "Sandro Tonali", "Sergej Milinković-Savić", "Hakan Çalhanoğlu", "Manuel Locatelli", "Marcelo Brozović"],
      "Defender": ["Theo Hernández", "Alessandro Bastoni", "Gleison Bremer", "Kim Min-jae", "Matthijs de Ligt", "Giovanni Di Lorenzo"]
    }
  };
  
  // Validate footballer name
  const validateFootballer = (name: string, row: number, col: number): boolean => {
    const rowCriterion = rowCriteria[row];
    const colCriterion = columnCriteria[col];
    const validPlayers = footballerDatabase[rowCriterion]?.[colCriterion] || [];
    return validPlayers.some(player => 
      player.toLowerCase() === name.trim().toLowerCase()
    );
  };

  useEffect(() => {
    fetchCurrentUser();
  }, [profile]);
  
  useEffect(() => {
    if (sessionId) {
      fetchSession();
      // Poll for updates every 2 seconds
      const interval = setInterval(() => fetchSession(), 2000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  
  const fetchCurrentUser = () => {
    console.log('fetchCurrentUser - Profile:', profile);
    
    // Try to get from Redux profile store first
    if (profile?.id) {
      console.log('Setting currentUserId from profile.id:', profile.id);
      setCurrentUserId(profile.id);
      return;
    }
    
    // Fallback to localStorage
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      console.log('Setting currentUserId from localStorage:', storedUserId);
      setCurrentUserId(parseInt(storedUserId));
    } else {
      console.warn('No userId found in profile or localStorage');
    }
  };

  const fetchSession = async () => {
    if (!sessionId) return;
    
    try {
      const response = await apiService.get(`/api/p2p-games/sessions/${sessionId}`);
      if (response.success && response.data) {
        setSession(response.data);
        
        // Determine currentUserId from session if not already set
        // Use a local variable to avoid stale closure issues
        let determinedUserId = currentUserId;
        
        if (!determinedUserId && response.data) {
          // Try to get from profile first
          if (profile?.id) {
            determinedUserId = profile.id;
            if (determinedUserId !== currentUserId) {
              setCurrentUserId(determinedUserId);
            }
          } else {
            // Try to match by username from profile
            if (profile?.username) {
              if (response.data.player1Username === profile.username) {
                determinedUserId = response.data.player1Id;
                setCurrentUserId(determinedUserId);
                console.log('✅ Matched! Setting currentUserId to player1Id:', determinedUserId);
              } else if (response.data.player2Username === profile.username) {
                determinedUserId = response.data.player2Id;
                setCurrentUserId(determinedUserId);
                console.log('✅ Matched! Setting currentUserId to player2Id:', determinedUserId);
              }
            } else {
              // Try to get username from JWT token
              try {
                const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
                if (token) {
                  const payload = JSON.parse(atob(token.split('.')[1]));
                  const tokenUsername = payload.sub;
                  
                  if (response.data.player1Username === tokenUsername) {
                    determinedUserId = response.data.player1Id;
                    setCurrentUserId(determinedUserId);
                    console.log('✅ Matched from JWT! Setting currentUserId to player1Id:', determinedUserId);
                  } else if (response.data.player2Username === tokenUsername) {
                    determinedUserId = response.data.player2Id;
                    setCurrentUserId(determinedUserId);
                    console.log('✅ Matched from JWT! Setting currentUserId to player2Id:', determinedUserId);
                  }
                }
              } catch (e) {
                console.error('Error parsing JWT token:', e);
              }
            }
          }
        }
        
        // Parse game state
        if (response.data.gameState) {
          try {
            const gameState = JSON.parse(response.data.gameState);
            console.log('Parsed game state:', gameState);
            
            // Check if it's Battleships
            if (response.data.gameName.toLowerCase().includes('battleships') || 
                response.data.gameName.toLowerCase().includes('battleship')) {
              setBattleshipsState(gameState);
              if (gameState.gameStatus) {
                setGameStatus(gameState.gameStatus);
              }
              if (gameState.currentTurn !== undefined && gameState.currentTurn !== null) {
                const turn = typeof gameState.currentTurn === 'string' 
                  ? parseInt(gameState.currentTurn) 
                  : Number(gameState.currentTurn);
                setCurrentTurn(turn);
              }
              if (gameState.winner !== undefined && gameState.winner !== null) {
                const winnerId = typeof gameState.winner === 'string'
                  ? parseInt(gameState.winner)
                  : Number(gameState.winner);
                setWinner(winnerId);
              }
            } else {
              // Tic-Tac-Toe game state
            if (gameState.board && Array.isArray(gameState.board)) {
              setGameBoard(gameState.board);
            } else {
              console.warn('Game state board is missing or invalid:', gameState.board);
            }
            
            if (gameState.currentTurn !== undefined && gameState.currentTurn !== null) {
              // Convert to number if it's a string
              const turn = typeof gameState.currentTurn === 'string' 
                ? parseInt(gameState.currentTurn) 
                : Number(gameState.currentTurn);
              setCurrentTurn(turn);
              console.log('Set currentTurn to:', turn, '(type:', typeof turn, ')');
            } else {
              console.warn('Game state currentTurn is missing or null');
            }
            
            if (gameState.gameStatus) {
              setGameStatus(gameState.gameStatus);
            }
            
            if (gameState.winner !== undefined && gameState.winner !== null) {
              const winnerId = typeof gameState.winner === 'string'
                ? parseInt(gameState.winner)
                : Number(gameState.winner);
              setWinner(winnerId);
              }
            }
          } catch (e) {
            console.error('Error parsing game state:', e);
            console.error('Raw gameState string:', response.data.gameState);
          }
        } else {
          console.warn('⚠️ Game state is null or empty! Game may not be initialized.');
          // If game is IN_PROGRESS but no game state, something is wrong
          if (response.data.status === 'IN_PROGRESS') {
            console.error('Game is IN_PROGRESS but gameState is null!');
          }
        }
        
        // Debug logging - use determinedUserId instead of currentUserId to avoid stale closure
        const parsedGameState = response.data.gameState ? JSON.parse(response.data.gameState) : null;
        const currentTurnFromState = parsedGameState?.currentTurn ? Number(parsedGameState.currentTurn) : null;
        const isMyTurnCalculated = determinedUserId !== null && currentTurnFromState !== null && 
          Number(currentTurnFromState) === Number(determinedUserId);
        
        console.log('🎮 Game Session Data:', {
          sessionId: response.data.id,
          currentUserId: determinedUserId,
          profileId: profile?.id,
          profileUsername: profile?.username,
          player1Id: response.data.player1Id,
          player1Username: response.data.player1Username,
          player2Id: response.data.player2Id,
          player2Username: response.data.player2Username,
          currentTurn: currentTurnFromState,
          isMyTurn: isMyTurnCalculated,
          gameState: parsedGameState,
          status: response.data.status,
          totalStake: response.data.totalStake
        });
      } else {
        setError('Failed to load game session');
      }
    } catch (err: any) {
      console.error('Error fetching session:', err);
      setError(err.message || 'Failed to load game session');
    } finally {
      setLoading(false);
    }
  };

  // Battleships handlers
  const handlePlaceShip = async (row: number, col: number) => {
    if (!session || !selectedShip || !battleshipsState) return;
    
    try {
      const response = await apiService.post(`/api/p2p-games/sessions/${session.id}/move`, {
        action: 'placeShip',
        row: row,
        col: col,
        size: selectedShip.size,
        horizontal: shipOrientation
      });
      
      if (response.success) {
        toast.success('Ship placed!');
        setTimeout(() => fetchSession(), 500);
        setSelectedShip(null);
      } else {
        toast.error(response.message || 'Failed to place ship');
      }
    } catch (err: any) {
      console.error('Error placing ship:', err);
      toast.error(err.message || err.response?.data?.message || 'Failed to place ship');
    }
  };
  
  const handleFireShot = async (row: number, col: number) => {
    if (!session || !battleshipsState) return;
    
    try {
      const response = await apiService.post(`/api/p2p-games/sessions/${session.id}/move`, {
        action: 'fireShot',
        row: row,
        col: col
      });
      
      if (response.success) {
        toast.success('Shot fired!');
        setTimeout(() => fetchSession(), 500);
      } else {
        toast.error(response.message || 'Failed to fire shot');
      }
    } catch (err: any) {
      console.error('Error firing shot:', err);
      toast.error(err.message || err.response?.data?.message || 'Failed to fire shot');
    }
  };
  
  // Handle football trivia move (for Tiki Taka Toe)
  const handleFootballMove = async () => {
    if (!selectedCell || !footballerInput.trim() || !session) return;
    
    const row = Math.floor(selectedCell / 3);
    const col = selectedCell % 3;
    
    // Validate footballer
    if (!validateFootballer(footballerInput, row, col)) {
      toast.error(`Invalid! "${footballerInput}" doesn't match ${rowCriteria[row]} ${columnCriteria[col]}`);
      return;
    }
    
    // Store the answer
    setPlayerAnswers(prev => ({ ...prev, [selectedCell]: footballerInput.trim() }));
    
    // Close modal
    setShowFootballModal(false);
    setFootballerInput('');
    const cellPosition = selectedCell;
    setSelectedCell(null);
    
    // Make the move (same as traditional tic-tac-toe)
    await handleMove(cellPosition);
  };

  const handleMove = async (position: number) => {
    if (!session) {
      toast.error('Session not loaded');
      return;
    }
    
    // Determine userId by matching username from JWT token (same logic as effectiveUserId)
    let userId: number | null = null;
    
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const tokenUsername = payload.sub;
        
        // Match username with session players
        if (session.player1Username === tokenUsername) {
          userId = session.player1Id;
        } else if (session.player2Username === tokenUsername) {
          userId = session.player2Id;
        }
      }
    } catch (e) {
      console.error('Error parsing JWT token in handleMove:', e);
    }
    
    // Fallback to profile username or profile.id
    if (!userId) {
      if (profile?.username) {
        if (session.player1Username === profile.username) {
          userId = session.player1Id;
        } else if (session.player2Username === profile.username) {
          userId = session.player2Id;
        }
      }
      if (!userId) {
        userId = profile?.id || currentUserId;
      }
    }
    
    if (!userId) {
      toast.error('Unable to determine your user ID');
      console.error('handleMove - No userId:', { profile: profile?.id, currentUserId, session });
      return;
    }
    
    // Check if it's the current player's turn
    const currentTurnNum = currentTurn !== null ? Number(currentTurn) : null;
    const userIdNum = Number(userId);
    if (currentTurnNum !== userIdNum) {
      toast.error(`Not your turn! It's player ${currentTurnNum}'s turn.`);
      console.warn('handleMove - Not user turn:', { userIdNum, currentTurnNum });
      return;
    }
    
    // Check if position is already taken
    if (gameBoard[position] !== '') {
      toast.error('This position is already taken!');
      return;
    }
    
    // Check if game is still in progress
    if (gameStatus !== 'IN_PROGRESS') {
      toast.error('Game has ended!');
      return;
    }
    
    console.log('Making move:', { sessionId: session.id, position, userId });
    
    try {
      const response = await apiService.post(`/api/p2p-games/sessions/${session.id}/move`, {
        position: position
      });
      
      console.log('Move response:', response);
      
      if (response.success) {
        toast.success('Move submitted!');
        // Refresh session to get updated game state
        setTimeout(() => fetchSession(), 500);
      } else {
        toast.error(response.message || response.error || 'Failed to submit move');
      }
    } catch (err: any) {
      console.error('Error submitting move:', err);
      toast.error(err.message || err.response?.data?.message || 'Failed to submit move');
    }
  };

  const handleCancel = async () => {
    if (!session?.id) return;
    
    if (!window.confirm('Are you sure you want to forfeit? Your stake will be lost.')) {
      return;
    }
    
    try {
      const response = await apiService.post(`/api/p2p-games/sessions/${session.id}/cancel`, {});
      if (response.success) {
        toast.success('Game cancelled');
        navigate('/p2p-games/challenges');
      } else {
        toast.error('Failed to cancel game');
      }
    } catch (err: any) {
      console.error('Error cancelling game:', err);
      toast.error(err.message || 'Failed to cancel game');
    }
  };

  if (loading && !session) {
    return (
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{
            width: 50,
            height: 50,
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
        <div style={{
          padding: 24,
          background: '#fed7d7',
          borderRadius: 12,
          color: '#c53030',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            {error || 'Session not found'}
          </h3>
          <button
            onClick={() => navigate('/p2p-games/challenges')}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Back to Challenges
          </button>
        </div>
      </div>
    );
  }

  // Determine effectiveUserId by matching username from JWT token with session players
  // This is more reliable than using profile.id which might be stale
  let effectiveUserId: number | null = currentUserId;
  
  if (session) {
    // Try to get username from JWT token first (most reliable)
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const tokenUsername = payload.sub;
        
        // Match username with session players
        if (session.player1Username === tokenUsername) {
          effectiveUserId = session.player1Id;
        } else if (session.player2Username === tokenUsername) {
          effectiveUserId = session.player2Id;
        }
      }
    } catch (e) {
      console.error('Error parsing JWT token:', e);
    }
    
    // Fallback: if still not determined, try profile username match
    if (!effectiveUserId && profile?.username) {
      if (session.player1Username === profile.username) {
        effectiveUserId = session.player1Id;
      } else if (session.player2Username === profile.username) {
        effectiveUserId = session.player2Id;
      }
    }
    
    // Last fallback: use profile.id or currentUserId
    if (!effectiveUserId) {
      effectiveUserId = profile?.id || currentUserId;
    }
  }
  
  const isPlayer1 = effectiveUserId !== null && effectiveUserId === session?.player1Id;
  
  // Handle type conversion for currentTurn comparison - ensure both are numbers
  const currentTurnNum = currentTurn !== null ? Number(currentTurn) : null;
  const effectiveUserIdNum = effectiveUserId !== null ? Number(effectiveUserId) : null;
  const isMyTurn = effectiveUserIdNum !== null && currentTurnNum !== null && currentTurnNum === effectiveUserIdNum;
  
  console.log('Turn Check:', {
    effectiveUserId,
    effectiveUserIdNum,
    currentUserId,
    currentTurn,
    currentTurnNum,
    isMyTurn,
    player1Id: session?.player1Id,
    player2Id: session?.player2Id,
    isPlayer1,
    profileId: profile?.id,
    profileUsername: profile?.username,
    sessionPlayer1Username: session?.player1Username,
    sessionPlayer2Username: session?.player2Username
  });
  
  const playerSymbol = isPlayer1 ? 'X' : 'O';
  const opponentSymbol = isPlayer1 ? 'O' : 'X';
  
  // Calculate total stake (use totalStake from DTO, or fallback to payoutAmount)
  const totalStake = session.totalStake || (session.payoutAmount > 0 ? session.payoutAmount : 0);

  // Determine game result message
  // Check both session.status and gameState.gameStatus
  const isGameCompleted = session?.status === 'COMPLETED' || gameStatus === 'COMPLETED';
  const isGameWon = isGameCompleted && (session?.winnerId || winner);
  const isGameDraw = isGameCompleted && session?.result === 'DRAW';
  
  let resultMessage = '';
  let isWinner = false;
  let payoutInfo = null;
  
  if (isGameWon && !isGameDraw) {
    const winnerId = session?.winnerId ? Number(session.winnerId) : (winner ? Number(winner) : null);
    const userIdNum = effectiveUserId !== null ? Number(effectiveUserId) : null;
    
    if (userIdNum !== null && winnerId !== null && winnerId === userIdNum) {
      resultMessage = '🎉 You Won!';
      isWinner = true;
      // Show payout amount if available
      if (session?.payoutAmount) {
        payoutInfo = `You received €${Number(session.payoutAmount).toFixed(2)}`;
      }
    } else {
      resultMessage = '😔 You Lost';
    }
  } else if (isGameDraw) {
    resultMessage = '🤝 Draw!';
    // Show refund amount if available
    if (session?.payoutAmount) {
      payoutInfo = `Both players refunded €${Number(session.payoutAmount).toFixed(2)} each`;
    }
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <button
          onClick={() => navigate('/p2p-games/challenges')}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            color: '#667eea',
            border: '1px solid #667eea',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16
          }}
        >
          <FaArrowLeft />
          Back
        </button>

        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: '#1a202c', marginBottom: 8 }}>
            {session.gameName}
          </h1>
          <p style={{ fontSize: 16, color: '#718096' }}>
            {session.player1Username} (X) vs {session.player2Username} (O)
          </p>
        </div>
      </div>

      {/* Prize Pool */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 16,
        padding: 24,
        color: 'white',
        marginBottom: 32,
        textAlign: 'center'
      }}>
        <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>
          Prize Pool
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <FaTrophy style={{ fontSize: 32 }} />
          <div style={{ fontSize: 48, fontWeight: 700 }}>
            €{totalStake.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Game Result Banner */}
      {resultMessage && (
        <div style={{
          padding: 24,
          background: isWinner ? '#c6f6d5' : isGameDraw ? '#feebc8' : '#fed7d7',
          border: `3px solid ${isWinner ? '#38a169' : isGameDraw ? '#d69e2e' : '#e53e3e'}`,
          borderRadius: 16,
          textAlign: 'center',
          marginBottom: 24,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: isWinner ? '#22543d' : isGameDraw ? '#744210' : '#c53030' }}>
            {resultMessage}
          </div>
          {payoutInfo && (
            <div style={{ fontSize: 18, fontWeight: 600, color: isWinner ? '#2d5016' : isGameDraw ? '#5a3d0a' : '#7a1c1c', marginTop: 12 }}>
              {payoutInfo}
            </div>
          )}
          {session?.platformFee && session.platformFee > 0 && (
            <div style={{ fontSize: 14, color: isWinner ? '#2d5016' : isGameDraw ? '#5a3d0a' : '#7a1c1c', marginTop: 8, opacity: 0.8 }}>
              Platform fee: €{Number(session.platformFee).toFixed(2)}
            </div>
          )}
        </div>
      )}

      {/* Turn Indicator */}
      {!isGameCompleted && gameStatus === 'IN_PROGRESS' && currentTurn !== null && (
        <div style={{
          padding: 16,
          background: isMyTurn ? '#e6fffa' : '#f7fafc',
          border: `2px solid ${isMyTurn ? '#81e6d9' : '#e2e8f0'}`,
          borderRadius: 12,
          textAlign: 'center',
          marginBottom: 24,
          fontSize: 18,
          fontWeight: 600,
          color: isMyTurn ? '#234e52' : '#718096'
        }}>
          {isMyTurn ? (
            `🎯 Your Turn (${playerSymbol})`
          ) : (
            `⏳ Waiting for ${currentTurnNum === session.player1Id ? session.player1Username : session.player2Username}...`
          )}
        </div>
      )}
      
      {/* Debug info if currentUserId not set */}
      {effectiveUserId === null && (
        <div style={{
          padding: 16,
          background: '#feebc8',
          border: '2px solid #f6ad55',
          borderRadius: 12,
          textAlign: 'center',
          marginBottom: 24,
          fontSize: 14,
          color: '#744210'
        }}>
          ⚠️ Unable to determine your player ID. Please refresh the page.
          <div style={{ fontSize: 12, marginTop: 8, opacity: 0.8 }}>
            Profile: {profile?.username || 'N/A'} (ID: {profile?.id || 'N/A'}) | Player1: {session.player1Username} (ID: {session.player1Id}) | Player2: {session.player2Username} (ID: {session.player2Id})
          </div>
        </div>
      )}
      
      {/* Debug info showing turn status */}
      {effectiveUserId !== null && (
        <div style={{
          padding: 12,
          background: '#e6f3ff',
          border: '1px solid #b3d9ff',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 12,
          color: '#004085'
        }}>
          <strong>Debug Info:</strong> Your ID: {effectiveUserId} | Current Turn: {currentTurn} | Is My Turn: {isMyTurn ? '✅ YES' : '❌ NO'} | Game Status: {gameStatus}
        </div>
      )}
      
      {/* Warning if game state not initialized */}
      {session.status === 'IN_PROGRESS' && !session.gameState && (
        <div style={{
          padding: 16,
          background: '#fed7d7',
          border: '2px solid #fc8181',
          borderRadius: 12,
          textAlign: 'center',
          marginBottom: 24,
          fontSize: 14,
          color: '#c53030'
        }}>
          ⚠️ Game state not initialized. The game may not have started properly.
        </div>
      )}

      {/* Game Board - Conditional Rendering */}
      {(session.gameName.toLowerCase().includes('battleships') || session.gameName.toLowerCase().includes('battleship')) ? (
        // Battleships Game Board
        battleshipsState && (
          <div style={{ maxWidth: 1000, margin: '0 auto 32px' }}>
            {gameStatus === 'SETUP' ? (
              // Ship Placement Phase
              <div>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a202c', marginBottom: 8 }}>
                    Place Your Ships
                  </h3>
                  <p style={{ fontSize: 14, color: '#718096' }}>
                    Select a ship and click on the board to place it. Ships: 1x Carrier (5), 1x Battleship (4), 2x Cruisers (3), 1x Destroyer (2)
                  </p>
                </div>
                
                {/* Ship Selection */}
                <div style={{ 
                  display: 'flex', 
                  gap: 12, 
                  justifyContent: 'center', 
                  marginBottom: 24,
                  flexWrap: 'wrap'
                }}>
                  {[
                    {size: 5, name: 'Carrier', count: 1},
                    {size: 4, name: 'Battleship', count: 1},
                    {size: 3, name: 'Cruiser', count: 2},
                    {size: 2, name: 'Destroyer', count: 1}
                  ].map((ship, idx) => {
                    const playerShips = effectiveUserId === session.player1Id 
                      ? battleshipsState.player1Ships || []
                      : battleshipsState.player2Ships || [];
                    const placedCount = playerShips.filter((s: any) => s.size === ship.size).length;
                    const remaining = ship.count - placedCount;
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          if (remaining > 0) {
                            setSelectedShip({size: ship.size, count: remaining});
                          }
                        }}
                        disabled={remaining === 0}
                        style={{
                          padding: '12px 20px',
                          background: selectedShip?.size === ship.size ? '#667eea' : remaining > 0 ? '#e2e8f0' : '#cbd5e0',
                          color: selectedShip?.size === ship.size ? 'white' : remaining > 0 ? '#2d3748' : '#718096',
                          border: 'none',
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: remaining > 0 ? 'pointer' : 'not-allowed',
                          opacity: remaining > 0 ? 1 : 0.6
                        }}
                      >
                        {ship.name} ({ship.size}) - {remaining} left
                      </button>
                    );
                  })}
                </div>
                
                {/* Orientation Toggle */}
                {selectedShip && (
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <button
                      onClick={() => setShipOrientation(!shipOrientation)}
                      style={{
                        padding: '8px 16px',
                        background: '#f7fafc',
                        border: '2px solid #e2e8f0',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      {shipOrientation ? '↔ Horizontal' : '↕ Vertical'}
                    </button>
                  </div>
                )}
                
                {/* Player's Board for Ship Placement */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div>
                    <h4 style={{ textAlign: 'center', marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                      Your Board
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `30px repeat(10, 35px)`,
                      gap: 2,
                      background: '#e2e8f0',
                      padding: 8,
                      borderRadius: 8
                    }}>
                      <div></div>
                      {[...Array(10)].map((_, col) => (
                        <div key={col} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, padding: 4 }}>
                          {col}
                        </div>
                      ))}
                      {[...Array(10)].map((_, row) => (
                        <React.Fragment key={row}>
                          <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, padding: 4 }}>
                            {row}
                          </div>
                          {[...Array(10)].map((_, col) => {
                            const position = row * 10 + col;
                            const playerBoard = effectiveUserId === session.player1Id
                              ? battleshipsState.player1Board || []
                              : battleshipsState.player2Board || [];
                            const cellValue = playerBoard[position] || 0;
                            const isShip = cellValue === 1;
                            
                            return (
                              <button
                                key={col}
                                onClick={() => {
                                  if (selectedShip) {
                                    handlePlaceShip(row, col);
                                  }
                                }}
                                disabled={!selectedShip || isShip}
                                style={{
                                  width: 35,
                                  height: 35,
                                  background: isShip ? '#667eea' : 'white',
                                  border: '1px solid #cbd5e0',
                                  borderRadius: 4,
                                  cursor: selectedShip && !isShip ? 'pointer' : 'not-allowed',
                                  fontSize: 12
                                }}
                              >
                                {isShip ? '🚢' : ''}
                              </button>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Shooting Phase
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                  {/* Player's Board (showing own ships and hits) */}
                  <div>
                    <h4 style={{ textAlign: 'center', marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                      Your Fleet
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `30px repeat(10, 35px)`,
                      gap: 2,
                      background: '#e2e8f0',
                      padding: 8,
                      borderRadius: 8
                    }}>
                      <div></div>
                      {[...Array(10)].map((_, col) => (
                        <div key={col} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, padding: 4 }}>
                          {col}
                        </div>
                      ))}
                      {[...Array(10)].map((_, row) => (
                        <React.Fragment key={row}>
                          <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, padding: 4 }}>
                            {row}
                          </div>
                          {[...Array(10)].map((_, col) => {
                            const position = row * 10 + col;
                            const playerBoard = effectiveUserId === session.player1Id
                              ? battleshipsState.player1Board || []
                              : battleshipsState.player2Board || [];
                            const cellValue = playerBoard[position] || 0;
                            const isShip = cellValue === 1;
                            const isHit = cellValue === 2;
                            
                            return (
                              <div
                                key={col}
                                style={{
                                  width: 35,
                                  height: 35,
                                  background: isHit ? '#ff7171' : isShip ? '#667eea' : '#e6f3ff',
                                  border: '1px solid #cbd5e0',
                                  borderRadius: 4,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 16
                                }}
                              >
                                {isHit ? '💥' : isShip ? '🚢' : ''}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  
                  {/* Opponent's Board (for shooting) */}
                  <div>
                    <h4 style={{ textAlign: 'center', marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                      Enemy Waters {isMyTurn && '(Your Turn)'}
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `30px repeat(10, 35px)`,
                      gap: 2,
                      background: '#e2e8f0',
                      padding: 8,
                      borderRadius: 8
                    }}>
                      <div></div>
                      {[...Array(10)].map((_, col) => (
                        <div key={col} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, padding: 4 }}>
                          {col}
                        </div>
                      ))}
                      {[...Array(10)].map((_, row) => (
                        <React.Fragment key={row}>
                          <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, padding: 4 }}>
                            {row}
                          </div>
                          {[...Array(10)].map((_, col) => {
                            const position = row * 10 + col;
                            const playerShots = effectiveUserId === session.player1Id
                              ? battleshipsState.player1Shots || []
                              : battleshipsState.player2Shots || [];
                            const shotValue = playerShots[position] || 0;
                            const isHit = shotValue === 1;
                            const isMiss = shotValue === 2;
                            
                            return (
                              <button
                                key={col}
                                onClick={() => {
                                  if (isMyTurn && !isHit && !isMiss && gameStatus === 'IN_PROGRESS') {
                                    handleFireShot(row, col);
                                  }
                                }}
                                disabled={!isMyTurn || isHit || isMiss || gameStatus !== 'IN_PROGRESS'}
                                style={{
                                  width: 35,
                                  height: 35,
                                  background: isHit ? '#ff7171' : isMiss ? '#cbd5e0' : 'white',
                                  border: '1px solid #cbd5e0',
                                  borderRadius: 4,
                                  cursor: isMyTurn && !isHit && !isMiss && gameStatus === 'IN_PROGRESS' ? 'pointer' : 'not-allowed',
                                  fontSize: 16,
                                  opacity: isMyTurn && !isHit && !isMiss && gameStatus === 'IN_PROGRESS' ? 1 : 0.7
                                }}
                              >
                                {isHit ? '💥' : isMiss ? '○' : ''}
                              </button>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      ) : session.gameName.toLowerCase().includes('tiki taka toe') ? (
        // Football Tic-Tac-Toe Board (Tiki Taka Toe)
        <div style={{ maxWidth: 600, margin: '0 auto 32px' }}>
          {/* Column Headers */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '120px repeat(3, 1fr)', 
            gap: 8, 
            marginBottom: 8 
          }}>
            <div></div>
            {columnCriteria.map((crit, idx) => (
              <div 
                key={idx} 
                style={{ 
                  textAlign: 'center', 
                  padding: '12px 8px', 
                  background: '#e2e8f0', 
                  borderRadius: 8, 
                  fontSize: 12, 
                  fontWeight: 700,
                  color: '#2d3748'
                }}
              >
                {crit}
              </div>
            ))}
          </div>
          
          {/* Board with Row Labels */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '120px repeat(3, 1fr)', 
            gap: 8 
          }}>
            {[0, 1, 2].map(row => (
              <React.Fragment key={row}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '12px 8px', 
                  background: '#e2e8f0', 
                  borderRadius: 8, 
                  fontSize: 12, 
                  fontWeight: 700,
                  color: '#2d3748'
                }}>
                  {rowCriteria[row]}
                </div>
                {[0, 1, 2].map(col => {
                  const position = row * 3 + col;
                  const cell = gameBoard[position];
                  const answer = playerAnswers[position];
                  return (
                    <button
                      key={position}
                      onClick={() => {
                        if (cell === '' && isMyTurn && gameStatus === 'IN_PROGRESS' && !isGameCompleted) {
                          setSelectedCell(position);
                          setShowFootballModal(true);
                        }
                      }}
                      disabled={cell !== '' || !isMyTurn || gameStatus !== 'IN_PROGRESS' || isGameCompleted}
                      style={{
                        aspectRatio: '1',
                        minHeight: 100,
                        background: 'white',
                        border: '2px solid #e2e8f0',
                        borderRadius: 8,
                        fontSize: cell ? 20 : 14,
                        fontWeight: 700,
                        color: cell === 'X' ? '#667eea' : '#f5576c',
                        cursor: cell === '' && isMyTurn && gameStatus === 'IN_PROGRESS' && !isGameCompleted ? 'pointer' : 'not-allowed',
                        opacity: cell === '' && isMyTurn && gameStatus === 'IN_PROGRESS' && !isGameCompleted ? 1 : cell === '' ? 0.5 : 1,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 8
                      }}
                      onMouseEnter={(e) => {
                        if (cell === '' && isMyTurn && gameStatus === 'IN_PROGRESS' && !isGameCompleted) {
                          e.currentTarget.style.background = '#edf2f7';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      {answer && (
                        <div style={{ 
                          fontSize: 10, 
                          color: '#718096', 
                          marginBottom: 4,
                          textAlign: 'center',
                          lineHeight: 1.2
                        }}>
                          {answer}
                        </div>
                      )}
                      {cell && (
                        <div style={{ fontSize: 32, fontWeight: 700 }}>
                          {cell}
                        </div>
                      )}
                      {!cell && !answer && isMyTurn && gameStatus === 'IN_PROGRESS' && (
                        <div style={{ fontSize: 11, color: '#a0aec0', textAlign: 'center' }}>
                          Click to play
                        </div>
                      )}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : (
        // Traditional Tic-Tac-Toe Board
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
        maxWidth: 400,
        margin: '0 auto 32px',
        background: '#f7fafc',
        padding: 16,
        borderRadius: 16
      }}>
        {gameBoard.map((cell, index) => (
          <button
            key={index}
            onClick={() => handleMove(index)}
            disabled={cell !== '' || !isMyTurn || gameStatus !== 'IN_PROGRESS' || isGameCompleted}
            style={{
              aspectRatio: '1',
              background: 'white',
              border: '2px solid #e2e8f0',
              borderRadius: 8,
              fontSize: 48,
              fontWeight: 700,
              color: cell === 'X' ? '#667eea' : '#f5576c',
              cursor: cell === '' && isMyTurn && gameStatus === 'IN_PROGRESS' && !isGameCompleted ? 'pointer' : 'not-allowed',
              opacity: cell === '' && isMyTurn && gameStatus === 'IN_PROGRESS' && !isGameCompleted ? 1 : cell === '' ? 0.5 : 1,
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (cell === '' && isMyTurn && gameStatus === 'IN_PROGRESS' && !isGameCompleted) {
                e.currentTarget.style.background = '#edf2f7';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
            }}
          >
            {cell}
          </button>
        ))}
      </div>
      )}
      
      {/* Football Trivia Modal */}
      {showFootballModal && selectedCell !== null && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: 16,
            padding: 24,
            maxWidth: 400,
            width: '90%',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, color: '#1a202c' }}>
              ⚽ Name a Footballer
            </h3>
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 14, color: '#718096', marginBottom: 8 }}>
                <strong>Row:</strong> <span style={{ color: '#2d3748', fontWeight: 600 }}>{rowCriteria[Math.floor(selectedCell / 3)]}</span>
              </p>
              <p style={{ fontSize: 14, color: '#718096', marginBottom: 16 }}>
                <strong>Column:</strong> <span style={{ color: '#2d3748', fontWeight: 600 }}>{columnCriteria[selectedCell % 3]}</span>
              </p>
              <p style={{ fontSize: 12, color: '#a0aec0', fontStyle: 'italic' }}>
                Enter a footballer who plays in {rowCriteria[Math.floor(selectedCell / 3)]} as a {columnCriteria[selectedCell % 3]}
              </p>
            </div>
            <input
              type="text"
              value={footballerInput}
              onChange={(e) => setFootballerInput(e.target.value)}
              placeholder="Enter footballer name..."
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 16,
                marginBottom: 16,
                outline: 'none'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleFootballMove();
                }
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleFootballMove}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Submit
              </button>
              <button
                onClick={() => {
                  setShowFootballModal(false);
                  setSelectedCell(null);
                  setFootballerInput('');
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#cbd5e0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#e2e8f0';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {isGameCompleted ? (
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <button
            onClick={() => navigate('/p2p-games/challenges')}
            style={{
              padding: '14px 28px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            Back to Challenges
          </button>
        </div>
      ) : (
        gameStatus === 'IN_PROGRESS' && (
          <button
            onClick={handleCancel}
            style={{
              width: '100%',
              maxWidth: 400,
            margin: '0 auto',
            display: 'block',
            padding: '12px 20px',
            background: '#fed7d7',
            color: '#c53030',
            border: 'none',
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Forfeit Game
        </button>
        )
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default P2PGamePlayer;

